/**
 * 筆記內容的純函式（desktop-app.md 10.1、10.7）。
 *
 * 這裡刻意不 import Tiptap：標題導出、純文字導出與 Markdown 匯出都只是
 * 走一遍 ProseMirror 的 doc JSON，讓資料層與備份不必依賴編輯器套件，
 * 測試也不需要真的把編輯器掛起來。
 */

export interface NoteNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: NoteNode[];
  text?: string;
  marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
}

export interface NoteDoc extends NoteNode {
  type: 'doc';
  content?: NoteNode[];
}

/** 新筆記的初始內容：一個空段落，游標才有地方落下。 */
export const EMPTY_NOTE_JSON = JSON.stringify({
  type: 'doc',
  content: [{ type: 'paragraph' }],
});

/** 標題只是清單上的一行字，太長會把清單撐爆。 */
export const NOTE_TITLE_MAX_LENGTH = 40;

export const UNTITLED_NOTE = '未命名筆記';

/** 會被當成「一個區塊」而在純文字裡自成一行的節點。 */
const BLOCK_TYPES = new Set([
  'paragraph',
  'heading',
  'codeBlock',
  'blockquote',
  'listItem',
  'taskItem',
  'horizontalRule',
]);

export function parseNoteDoc(contentJson: string): NoteDoc {
  let parsed: unknown;
  try {
    parsed = JSON.parse(contentJson);
  } catch {
    return { type: 'doc', content: [] };
  }
  if (!parsed || typeof parsed !== 'object') return { type: 'doc', content: [] };
  const candidate = parsed as NoteNode;
  if (candidate.type !== 'doc') return { type: 'doc', content: [] };
  return candidate as NoteDoc;
}

export function serializeNoteDoc(doc: NoteDoc): string {
  return JSON.stringify(doc);
}

export function isEmptyNoteDoc(doc: NoteDoc): boolean {
  return extractPlainText(doc).trim() === '' && collectNoteImageIds(doc).size === 0;
}

export function collectNoteImageIds(doc: NoteNode): Set<string> {
  const ids = new Set<string>();
  const walk = (node: NoteNode) => {
    if (node.type === 'noteImage' && typeof node.attrs?.['imageId'] === 'string') {
      ids.add(node.attrs['imageId']);
    }
    for (const child of node.content ?? []) walk(child);
  };
  walk(doc);
  return ids;
}

/**
 * 導出純文字：每個區塊一行，硬換行也算一行。
 *
 * 空白區塊不產生空行——使用者為了排版按的空行不該讓搜尋摘要與標題往下掉。
 */
export function extractPlainText(doc: NoteDoc): string {
  const lines: string[] = [];
  collectLines(doc, lines);
  return lines.filter((line) => line.trim() !== '').join('\n');
}

function collectLines(node: NoteNode, lines: string[]): void {
  if (node.type === 'text') {
    appendInline(lines, node.text ?? '');
    return;
  }
  if (node.type === 'hardBreak') {
    lines.push('');
    return;
  }

  const isBlock = BLOCK_TYPES.has(node.type);
  // 區塊起始就先開一行，之後的行內文字都接在它後面。
  if (isBlock) lines.push('');

  for (const child of node.content ?? []) collectLines(child, lines);
}

function appendInline(lines: string[], text: string): void {
  if (lines.length === 0) lines.push('');
  lines[lines.length - 1] += text;
}

/** 標題自動取內文第一行，不要求使用者命名（10.2）。 */
export function deriveNoteTitle(plainText: string): string {
  const first = plainText.split('\n').find((line) => line.trim() !== '') ?? '';
  return first.trim().slice(0, NOTE_TITLE_MAX_LENGTH);
}

/** 清單上顯示用；專屬標題優先，沒有才退回內文第一行。 */
export function noteDisplayTitle(
  title: string,
  customTitle: string | null | undefined = null,
): string {
  const resolved = customTitle?.trim() || title.trim();
  return resolved === '' ? UNTITLED_NOTE : resolved;
}

export interface NoteSummary {
  title: string;
  plainText: string;
}

/**
 * 由 contentJson 一次算出標題與純文字。
 * 兩者必須同時寫入，否則清單顯示的標題會跟搜尋到的內容對不起來。
 */
export function summarizeNote(contentJson: string): NoteSummary {
  const plainText = extractPlainText(parseNoteDoc(contentJson));
  return { title: deriveNoteTitle(plainText), plainText };
}

/* -------------------------------------------------------------------------
 * Markdown 匯出（10.1）
 *
 * 這是**有損轉換**：Markdown 規格沒有顏色與字體，所以 noteColor、noteFont
 * 與底線一律只保留文字。UI 上必須說明，不要讓使用者以為匯出等於備份。
 * ---------------------------------------------------------------------- */

export function toMarkdown(doc: NoteDoc): string {
  const blocks = (doc.content ?? []).flatMap((node) => renderBlock(node, 0));
  return blocks.length === 0 ? '' : `${blocks.join('\n\n')}\n`;
}

function renderBlock(node: NoteNode, depth: number): string[] {
  const indent = '  '.repeat(depth);

  switch (node.type) {
    case 'noteImage': {
      const id = node.attrs?.['imageId'];
      if (typeof id !== 'string' || !/^[0-9a-f-]{36}$/.test(id)) return [];
      const width = node.attrs?.['displayWidth'];
      const height = node.attrs?.['displayHeight'];
      const dimensions =
        typeof width === 'number' && typeof height === 'number' && width > 0 && height > 0
          ? ` width="${Math.round(width)}" height="${Math.round(height)}"`
          : '';
      return [`<img src="images/${id}.png"${dimensions} alt="筆記圖片">`];
    }
    case 'heading': {
      const level = clampLevel(node.attrs?.['level']);
      return [`${'#'.repeat(level)} ${renderInline(node.content ?? [])}`];
    }
    case 'paragraph': {
      const text = renderInline(node.content ?? []);
      return text === '' ? [] : [`${indent}${text}`];
    }
    case 'codeBlock': {
      const language = typeof node.attrs?.['language'] === 'string' ? node.attrs['language'] : '';
      const body = (node.content ?? []).map((child) => child.text ?? '').join('');
      return [`\`\`\`${language}\n${body}\n\`\`\``];
    }
    case 'horizontalRule':
      return ['---'];
    case 'blockquote':
      return [
        (node.content ?? [])
          .flatMap((child) => renderBlock(child, depth))
          .map((line) => `> ${line}`)
          .join('\n'),
      ];
    case 'bulletList':
      return [renderList(node, depth, () => '- ')];
    case 'orderedList':
      return [renderList(node, depth, (index) => `${index + 1}. `)];
    default:
      return (node.content ?? []).flatMap((child) => renderBlock(child, depth));
  }
}

/** 清單整體算一個區塊，項目之間只隔一個換行，不空一整行。 */
function renderList(node: NoteNode, depth: number, marker: (index: number) => string): string {
  const indent = '  '.repeat(depth);
  return (node.content ?? [])
    .map((item, index) => {
      const lines = (item.content ?? []).flatMap((child) => renderBlock(child, depth + 1));
      const [first = '', ...rest] = lines.map((line) => line.trimStart());
      const head = `${indent}${marker(index)}${first}`;
      return [head, ...rest.map((line) => `${indent}  ${line}`)].join('\n');
    })
    .join('\n');
}

function clampLevel(value: unknown): number {
  const level = typeof value === 'number' ? value : 1;
  return Math.min(6, Math.max(1, Math.trunc(level)));
}

function renderInline(nodes: NoteNode[]): string {
  return nodes
    .map((node) => {
      if (node.type === 'hardBreak') return '  \n';
      if (node.type !== 'text') return renderInline(node.content ?? []);

      let text = node.text ?? '';
      for (const mark of node.marks ?? []) {
        switch (mark.type) {
          case 'bold':
            text = `**${text}**`;
            break;
          case 'italic':
            text = `*${text}*`;
            break;
          case 'strike':
            text = `~~${text}~~`;
            break;
          case 'code':
            text = `\`${text}\``;
            break;
          case 'link': {
            const href = typeof mark.attrs?.['href'] === 'string' ? mark.attrs['href'] : '';
            if (href) text = `[${text}](${href})`;
            break;
          }
          // underline、textStyle（顏色與字體）在 Markdown 沒有對應語法，只留文字。
          default:
            break;
        }
      }
      return text;
    })
    .join('');
}

/** Windows 檔名不允許的字元。 */
const UNSAFE_FILENAME_CHARS = /[\\:*?"<>|/]/g;

/** 檔名要能一眼看出是哪一篇、什麼時候匯出的。 */
export function noteMarkdownFileName(title: string, at: Date = new Date()): string {
  const pad = (value: number) => value.toString().padStart(2, '0');
  const stamp =
    `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}` +
    `-${pad(at.getHours())}${pad(at.getMinutes())}`;
  const safe = noteDisplayTitle(title).replace(UNSAFE_FILENAME_CHARS, '-');
  return `${safe}-${stamp}.md`;
}
